import { Box, Link as MuiLink, TextField } from '@mui/material';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import type { TicketListItemDto } from '@app/shared';
import { DataTable } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { FilterPanel } from '../../components/FilterPanel';
import { StatusChip } from '../../components/StatusChip';
import { formatDateTime } from '../../lib/format';
import { useListState } from '../../lib/useListState';
import { useAuth } from '../auth/AuthProvider';
import { ShowTicketDialog } from './TicketDialogs';
import { useCustomerTickets } from './api';

/**
 * A customer's tickets (PC87), inside the customer workspace. Filters and columns as the
 * reference: Date From, Date To, Ticket Number; Ticket Number, Title, Description,
 * Assigned To, Status, Created By, Created Date.
 */
export function CustomerTicketsPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const list = useListState({ dateFrom: '', dateTo: '', ticketNo: '' }, 10);
  const tickets = useCustomerTickets(id, list.params);
  const [showing, setShowing] = useState<string>();

  const columns: Column<TicketListItemDto>[] = [
    {
      key: 'ticketNo',
      header: 'Ticket Number',
      nowrap: true,
      // Opening a ticket is part of the Tickets module.
      render: (t) =>
        can('PC722') ? (
          <MuiLink component="button" type="button" onClick={() => setShowing(t.id)}>
            {t.ticketNo}
          </MuiLink>
        ) : (
          t.ticketNo
        ),
    },
    { key: 'title', header: 'Title', render: (t) => t.title },
    { key: 'description', header: 'Description', render: (t) => t.description },
    { key: 'assignedTo', header: 'Assigned To', render: (t) => t.assignedTo },
    { key: 'status', header: 'Status', render: (t) => <StatusChip status={t.status} /> },
    { key: 'createdBy', header: 'Created By', render: (t) => t.createdBy?.loginId ?? '' },
    {
      key: 'createdAt',
      header: 'Created Date',
      nowrap: true,
      render: (t) => formatDateTime(t.createdAt),
    },
  ];

  return (
    <Box>
      <FilterPanel onSearch={list.search} onReset={list.reset}>
        <TextField
          type="date"
          label="Date From"
          value={list.draft.dateFrom}
          onChange={(e) => list.setField('dateFrom', e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          type="date"
          label="Date To"
          value={list.draft.dateTo}
          onChange={(e) => list.setField('dateTo', e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label="Ticket Number"
          value={list.draft.ticketNo}
          onChange={(e) => list.setField('ticketNo', e.target.value)}
        />
      </FilterPanel>
      <DataTable
        label="Tickets"
        columns={columns}
        rows={tickets.data?.data}
        rowKey={(t) => t.id}
        loading={tickets.isFetching}
        error={tickets.error}
        onRetry={() => void tickets.refetch()}
        emptyText="No tickets have been raised for this customer."
        page={list.page}
        pageSize={list.pageSize}
        total={tickets.data?.total ?? 0}
        onPageChange={list.setPage}
        onPageSizeChange={list.setPageSize}
      />
      <ShowTicketDialog ticketId={showing} onClose={() => setShowing(undefined)} />
    </Box>
  );
}
